import React, { useEffect, useState } from "react"
import "./Kpi.css"
import PropTypes from "prop-types"
import { globals, ca_svg_flatten, icon_svg_room_flatten, icon_svg_room, le_cercle_svg_stream } from "../Globals/Globals";

/**
 * KPI card component (375x340px)
 * - Icon (top)
 * - Number (big)
 * - Small separator line
 * - Uppercase title (single line, ellipsis)
 * - Lowercase text (single line, ellipsis)
 */
const Kpi = ({
    icon,
    icon_style,        // string (img src or inline SVG markup) or React node (SVG element)
    value,       // number or string e.g. "34%"
    unit,
    title,       // string, displayed uppercase
    text,        // string, displayed lowercase
    color = "#E38B1A", // accent color used for border, value, and icon
    rotation = 0 // degrees; if 0, icon gently blinks; if >0, rotates 0->rotation smoothly
}) => {
    // Replace stroke colors in inline SVG markup with provided color
    const applyStrokeColor = (svgMarkup, newColor) => {
        if (typeof svgMarkup !== "string" || !newColor) return svgMarkup;
        let out = svgMarkup;
        // Replace CSS style declarations: stroke: <value>;
        out = out.replace(/(stroke:\s*)(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|[a-zA-Z]+)(\s*;)/gi, `$1${newColor}$3`);
        // Replace SVG attributes: stroke="<value>"
        out = out.replace(/(stroke=)(["'])([^"']+)(\2)/gi, `$1"${newColor}"`);
        return out;
    };

    // Replace fill colors in inline SVG markup with provided color
    const applyFillColor = (svgMarkup, newColor) => {
        if (typeof svgMarkup !== "string" || !newColor) return svgMarkup;
        let out = svgMarkup;
        // Replace CSS style declarations: stroke: <value>;
        out = out.replace(/(fill:\s*)(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|[a-zA-Z]+)(\s*;)/gi, `$1${newColor}$3`);
        // Replace SVG attributes: fill="<value>"
        out = out.replace(/(fill=)(["'])([^"']+)(\2)/gi, `$1"${newColor}"`);
        return out;
    };

    const effectiveIcon = icon?icon:
                              icon_style?
                              icon_style.includes("3-")
                              ? ca_svg_flatten
                              : icon_style.includes("2-")
                              ? icon_svg_room_flatten
                              : le_cercle_svg_stream:le_cercle_svg_stream; // default and for any '1-' or unknown style;
    const [iconSrc, setIconSrc] = useState(
        typeof effectiveIcon === "string" ? effectiveIcon : ""
    );
    const [inlineSvg, setInlineSvg] = useState(null);
    const [rotateAngle, setRotateAngle] = useState(0);

    const isSvgString =
        typeof effectiveIcon === "string" && effectiveIcon.trim().startsWith("<svg");

    // Trigger smooth rotation on mount/update when rotation > 0
    useEffect(() => {
        if (rotation > 0) {
            // allow initial paint at 0deg then animate to target
            const raf = requestAnimationFrame(() => setRotateAngle(rotation));
            return () => cancelAnimationFrame(raf);
        } else {
            setRotateAngle(0);
        }
    }, [rotation]);

    // If icon is a URL to an SVG, fetch and inline it to apply color
    useEffect(() => {
        let aborted = false;
        const src = iconSrc || "";
        const looksLikeSvgUrl = typeof src === "string" && src.trim().endsWith(".svg") && !isSvgString;
        if (!looksLikeSvgUrl) {
            console.log("SVG content - can be colored")
            setInlineSvg(null);
            return;
        }
        else{
            console.log("SVG string - cannot be colored !")
        }

        return () => { aborted = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iconSrc]);

    const renderIcon = () => {
        // React element (preferably an inline <svg/>)
        if (React.isValidElement(effectiveIcon)) {
            if (globals.parameters.debugging_svg) title = "inline <svg/>"
            return <div className="kpi-icon kpi-icon-svg">{effectiveIcon}</div>;
        }
        // Inline SVG markup provided as string
        if (isSvgString) {
            const processed = icon_style?icon_style.includes("1-")?applyFillColor(effectiveIcon, color):applyStrokeColor(effectiveIcon, color):applyStrokeColor(effectiveIcon, color);

            if (globals.parameters.debugging_svg) {
                title = "Inline SVG"
                console.log(`Inline SVG - "${text}" - effectiveIcon->`,effectiveIcon)
                console.log(`Inline SVG - "${text}" - processed->`,processed)
            }            
            return (
                <div
                    className="kpi-icon kpi-icon-svg"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: processed }}
                />
            );
        }
        // Inlined external SVG fetched by URL
        if (inlineSvg) {
            if (globals.parameters.debugging_svg) title = "external SVG fetched by URL"
            return (
                <div
                    className="kpi-icon kpi-icon-svg"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: inlineSvg }}
                />
            );
        }
        // Fallback to img src when a non-SVG string is provided
        if (iconSrc) {
            if (globals.parameters.debugging_svg) {
                title = "Fallback to img src"
                console.log("Fallback iconSrc->",iconSrc)
            }
            return (
                <img
                    className="kpi-icon kpi-icon-img"
                    src={iconSrc}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    onError={() => {
                        if (iconSrc) setIconSrc("");
                    }}
                />
            );
        }
        // No icon provided
        return <div className="kpi-icon" aria-hidden="true" />;
    };

    const iconContainerClass =
        rotation > 0 ? "kpi-icon-container kpi-icon-rotate" : "kpi-icon-container kpi-icon-blink";

    return (
        <div className="kpi-card" style={{ borderColor: color }}>
            <div
                className={iconContainerClass}
                style={{
                    // Use CSS currentColor to color inline SVG
                    color,
                    transform: rotation > 0 ? `rotate(${rotateAngle}deg)` : undefined
                }}
            >
                {renderIcon()}
            </div>

            <div className="kpi-value" style={{ color }}>
                {value}{unit}
            </div>

            <div className="kpi-separator" style={{ backgroundColor: color }} />

            <div className="kpi-title" title={title}>
                {String(title || "").toUpperCase()}
            </div>

            <div className="kpi-text" title={text}>
                {String(text || "").toLowerCase()}
            </div>
        </div>
    );
};

export default Kpi

Kpi.propTypes = {
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unit: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    title: PropTypes.string,
    text: PropTypes.string,
    color: PropTypes.string,
    rotation: PropTypes.number,
};

Kpi.defaultProps = {
    color: "#E38B1A",
    rotation: 0,
};